import { useWeb3React } from '@web3-react/core'
import Loader from 'components/Icons/LoadingSpinner'
import { Portal } from 'components/Portal'
import WalletDropdown, { useWalletDrawer } from 'components/WalletDropdown'
import PrefetchBalancesWrapper from 'components/WalletDropdown/PrefetchBalancesWrapper'
import { useGetConnection } from 'connection'
import { useCallback, useMemo } from 'react'
import { useAppSelector } from 'state/hooks'

import { isTransactionRecent, useAllTransactions } from '../../state/transactions/hooks'
import { TransactionDetails } from '../../state/transactions/types'
import { shortenAddress } from '../../utils'
import StatusIcon from '../Identicon/StatusIcon'
import { RowBetween } from '../Row'
import {
  AddressAndChevronContainer,
  NetworkIcon,
  StyledConnectButton,
  Text,
  Web3StatusConnected,
  Web3StatusConnectWrapper,
  Web3StatusError,
} from './styles'

// we want the latest one to come first, so return negative if a is after b
function newTransactionsFirst(a: TransactionDetails, b: TransactionDetails) {
  return b.addedTime - a.addedTime
}

function Web3StatusInner() {
  const { account, connector, chainId, ENSName } = useWeb3React()
  const getConnection = useGetConnection()
  const connection = getConnection(connector)
  const [, toggleWalletDrawer] = useWalletDrawer()
  const handleWalletDropdownClick = useCallback(() => {
    toggleWalletDrawer()
  }, [toggleWalletDrawer])

  const error = useAppSelector((state) => state.connection.errorByConnectionType[getConnection(connector).type])

  const allTransactions = useAllTransactions()

  const sortedRecentTransactions = useMemo(() => {
    const txs = Object.values(allTransactions)
    return txs.filter(isTransactionRecent).sort(newTransactionsFirst)
  }, [allTransactions])

  const pending = sortedRecentTransactions.filter((tx) => !tx.receipt).map((tx) => tx.hash)

  const hasPendingTransactions = !!pending.length

  if (!chainId) {
    return null
  } else if (error) {
    return (
      <Web3StatusError onClick={handleWalletDropdownClick}>
        <NetworkIcon />
        <Text>
          <>Error</>
        </Text>
      </Web3StatusError>
    )
  } else if (account) {
    return (
      <Web3StatusConnected
        data-testid="web3-status-connected"
        onClick={handleWalletDropdownClick}
        pending={hasPendingTransactions}
      >
        {!hasPendingTransactions && <StatusIcon size={16} connection={connection} showMiniIcons={false} />}
        {hasPendingTransactions ? (
          <RowBetween>
            <Text>
              <>{pending?.length} Pending</>
            </Text>{' '}
            <Loader stroke="white" />
          </RowBetween>
        ) : (
          <AddressAndChevronContainer>
            <Text>{ENSName || shortenAddress(account)}</Text>
          </AddressAndChevronContainer>
        )}
      </Web3StatusConnected>
    )
  } else {
    return (
      <Web3StatusConnectWrapper
        tabIndex={0}
        faded={!account}
        onKeyPress={(e) => e.key === 'Enter' && handleWalletDropdownClick()}
        onClick={handleWalletDropdownClick}
      >
        <StyledConnectButton tabIndex={-1} data-testid="navbar-connect-wallet">
          <>Connect</>
        </StyledConnectButton>
      </Web3StatusConnectWrapper>
    )
  }
}

// eslint-disable-next-line import/no-unused-modules
export default function Web3Status() {
  return (
    <PrefetchBalancesWrapper>
      <Web3StatusInner />
      <Portal>
        <WalletDropdown />
      </Portal>
    </PrefetchBalancesWrapper>
  )
}
